import {
  type AcknowledgePatientMessageRequest,
  AcknowledgePatientMessageRequestSchema,
  type AcknowledgePatientMessageResponse,
  AcknowledgePatientMessageResponseSchema,
  type AppointmentPatientMessages,
  AppointmentPatientMessagesSchema,
  type PatientMessageAttentionList,
  PatientMessageAttentionListSchema,
  type PatientMessagingChart,
  PatientMessagingChartSchema,
  type RecordPatientMessagingDecisionRequest,
  RecordPatientMessagingDecisionRequestSchema,
  type RecordPatientMessagingDecisionResponse,
  RecordPatientMessagingDecisionResponseSchema,
  type ResendPatientMessageRequest,
  ResendPatientMessageRequestSchema,
  type ResendPatientMessageResponse,
  ResendPatientMessageResponseSchema,
} from "@emr/contracts";
import { z } from "zod";
import { SchedulingRequestError, schedulingRequestError } from "../demo/schedulingErrors";

/**
 * Patient texts and emails (PA-7 / EXECDEV-185) on the staff scheduling lane. Every route acts on
 * the client's own site (its `x-site-id`), so none takes a site argument. Requests are checked
 * against the contract before they leave and responses after they arrive; errors are
 * SchedulingRequestError, whose `detail` names a refusal (`message_in_flight`,
 * `message_recipient_stopped`, `message_changed`... see SchedulingErrorDetail).
 *
 * Every read is audited on the server, so callers fetch it when a panel opens, never on the
 * schedule poll. The Attention read is polled, but only while the durable schedule lane answers.
 * No response carries a message body, a link, a token or a full phone number or address.
 *
 * - GET  /v1/scheduling/appointments/:id/messages              the booking's messages
 * - POST /v1/scheduling/appointments/:id/messages/resend       send one again (new message)
 * - POST /v1/scheduling/appointments/:id/messages/acknowledge  "Mark done"
 * - GET  /v1/scheduling/messages/attention                     messages that need a person
 * - GET  /v1/patients/:patientId/messaging                     the chart's opt-out state
 * - POST /v1/scheduling/appointments/:id/messaging/decisions   staff record "Patient agreed" or
 *                                                              "Patient said no" on a booking
 *                                                              (a walk-in's row included; PA-8)
 * - POST /v1/patients/:patientId/messaging/decisions           the same on the chart (PA-8)
 *
 * Correcting a booking's phone or email (owner decision O4) is a schedule write, on the
 * scheduling client (`correctBookingContact`), because it bumps the booking's version.
 */
export function createPatientMessagingClient(
  request: (path: string, body?: unknown, signal?: AbortSignal) => Promise<Response>,
) {
  async function json<T extends z.ZodType>(
    path: string,
    schema: T,
    fallbackMessage: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<z.output<T>> {
    const response = await request(path, body, signal);
    if (!response.ok) throw await schedulingRequestError(response, fallbackMessage);
    return schema.parse(await response.json());
  }
  /** Durable appointment and chart ids are uuids; anything else never reaches a URL. */
  const appointmentPath = (appointmentId: string, suffix: string) =>
    `/v1/scheduling/appointments/${encodeURIComponent(z.string().uuid().parse(appointmentId))}${suffix}`;
  const patientPath = (patientId: string, suffix: string) =>
    `/v1/patients/${encodeURIComponent(z.string().uuid().parse(patientId))}${suffix}`;

  return {
    /** The booking's texts and emails, newest first, with what staff may do with each. */
    async getAppointmentPatientMessages(
      appointmentId: string,
      signal?: AbortSignal,
    ): Promise<AppointmentPatientMessages> {
      return json(
        appointmentPath(appointmentId, "/messages"),
        AppointmentPatientMessagesSchema,
        "The booking's texts and emails could not be loaded.",
        undefined,
        signal,
      );
    },

    /**
     * Sends a message of the same kind and channel again, as a new message with a fresh link, to
     * the booking's current contact. Retrying with the same key returns the message it created.
     */
    async resendPatientMessage(
      appointmentId: string,
      input: ResendPatientMessageRequest,
      signal?: AbortSignal,
    ): Promise<ResendPatientMessageResponse> {
      return json(
        appointmentPath(appointmentId, "/messages/resend"),
        ResendPatientMessageResponseSchema,
        "The message could not be resent.",
        ResendPatientMessageRequestSchema.parse(input),
        signal,
      );
    },

    /** Marks a message that needed a person as dealt with, so it leaves Attention and the row. */
    async acknowledgePatientMessage(
      appointmentId: string,
      input: AcknowledgePatientMessageRequest,
      signal?: AbortSignal,
    ): Promise<AcknowledgePatientMessageResponse> {
      return json(
        appointmentPath(appointmentId, "/messages/acknowledge"),
        AcknowledgePatientMessageResponseSchema,
        "The message could not be marked done.",
        AcknowledgePatientMessageRequestSchema.parse(input),
        signal,
      );
    },

    /** Texts and emails that need a person, for bookings still ahead (Attention). */
    async getPatientMessageAttention(signal?: AbortSignal): Promise<PatientMessageAttentionList> {
      return json(
        "/v1/scheduling/messages/attention",
        PatientMessageAttentionListSchema,
        "Texts and emails that need attention could not be loaded.",
        undefined,
        signal,
      );
    },

    /** Whether the clinic may text or email the chart's numbers and addresses (STOP / START). */
    async getPatientMessaging(
      patientId: string,
      signal?: AbortSignal,
    ): Promise<PatientMessagingChart> {
      return json(
        patientPath(patientId, "/messaging"),
        PatientMessagingChartSchema,
        "The patient's text and email settings could not be loaded.",
        undefined,
        signal,
      );
    },

    /**
     * Records the patient's spoken decision about texts and emails on a booking (PA-8, owner
     * decision O5): "Patient agreed" after staff read the clinic's messaging notice, or "Patient
     * said no". Once the booking is linked to a chart it covers the patient's visits at this
     * clinic (owner decision O1). The same key returns the decision it recorded (created false).
     */
    async recordAppointmentMessagingDecision(
      appointmentId: string,
      input: RecordPatientMessagingDecisionRequest,
      signal?: AbortSignal,
    ): Promise<RecordPatientMessagingDecisionResponse> {
      return json(
        appointmentPath(appointmentId, "/messaging/decisions"),
        RecordPatientMessagingDecisionResponseSchema,
        "The patient's answer about texts and emails could not be recorded.",
        RecordPatientMessagingDecisionRequestSchema.parse(input),
        signal,
      );
    },

    /** The same decision recorded on the chart: it covers the patient's visits at this clinic. */
    async recordPatientMessagingDecision(
      patientId: string,
      input: RecordPatientMessagingDecisionRequest,
      signal?: AbortSignal,
    ): Promise<RecordPatientMessagingDecisionResponse> {
      return json(
        patientPath(patientId, "/messaging/decisions"),
        RecordPatientMessagingDecisionResponseSchema,
        "The patient's answer about texts and emails could not be recorded.",
        RecordPatientMessagingDecisionRequestSchema.parse(input),
        signal,
      );
    },
  };
}

export type PatientMessagingClient = ReturnType<typeof createPatientMessagingClient>;

/** The route is not there: the durable schedule lane (or this agent-api) has no messaging yet. */
export function isPatientMessagingUnavailable(error: unknown): boolean {
  return error instanceof SchedulingRequestError && error.status === 404 && !error.detail;
}

/** The signed-in role may not read or change this (401/403). */
export function isPatientMessagingRefusal(error: unknown): boolean {
  return error instanceof SchedulingRequestError && (error.status === 401 || error.status === 403);
}
